import React, { useEffect, useState } from "react";
import axios from "axios";
import { CheckCircle2, Send, Loader2, Warehouse, Truck, CreditCard, Landmark, Copy, Lock, Clock, CalendarDays, Store, CalendarClock, CalendarCheck, MessagesSquare } from "lucide-react";
import { format } from "date-fns";
import { Calendar } from "../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { toast } from "sonner";
import { IMAGES, SERVICE_OPTIONS, MOBILE_SERVICE_OPTIONS, OPENING_HOURS, ONLINE_PRICES } from "../../mock";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import Reveal from "./Reveal";
import EnquiryForm from "./EnquiryForm";
import { Badge } from "./Services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const empty = { name: "", email: "", phone: "", make: "", model: "", year: "", service: SERVICE_OPTIONS[0], details: "", service_type: "workshop", payment_method: "card", workshop_pay: "none", preferred_date: null, preferred_time: "" };
const endOf = (t) => `${String(parseInt(t, 10) + 1).padStart(2, "0")}:00`;
const fmt = (n) => (Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(2));
const fieldCls = "h-11 rounded-lg border-white/10 bg-[#0b0c0f] text-white placeholder:text-white/35 focus-visible:ring-brand/60 focus-visible:ring-offset-0";

const Label = ({ children }) => <label className="mb-2 block text-[12px] font-semibold uppercase tracking-[0.06em] text-white/60">{children}</label>;

const QuoteForm = () => {
  const [f, setF] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState({ deposit: 50, online: ONLINE_PRICES, offers: {} });
  const [closures, setClosures] = useState([]);
  useEffect(() => {
    axios.get(`${API}/config`).then(({ data }) => setCfg({ deposit: data.deposit_amount, online: data.online_prices, offers: data.offers || {} })).catch(() => {});
    axios.get(`${API}/closures`).then(({ data }) => setClosures(data)).catch(() => {});
  }, []);
  const DEPOSIT = cfg.deposit;
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });

  useEffect(() => {
    const onSelect = (e) => { setMode("booking"); setF((prev) => ({
      ...prev,
      service: e.detail,
      // mobile only offers diagnostics; any other service switches back to a workshop visit
      service_type: MOBILE_SERVICE_OPTIONS.includes(e.detail) ? prev.service_type : "workshop",
    })); };
    const onMode = (e) => setMode(e.detail === "enquiry" ? "enquiry" : "booking");
    window.addEventListener("evt:select-service", onSelect);
    window.addEventListener("evt:form-mode", onMode);
    return () => { window.removeEventListener("evt:select-service", onSelect); window.removeEventListener("evt:form-mode", onMode); };
  }, []);

  const [bankInfo, setBankInfo] = useState(null);
  const [mode, setMode] = useState("booking");
  const [slots, setSlots] = useState([]);
  const [booked, setBooked] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [closedReason, setClosedReason] = useState("");
  const [calOpen, setCalOpen] = useState(false);

  useEffect(() => {
    if (!f.preferred_date) { setSlots([]); setBooked([]); return; }
    setSlotsLoading(true);
    axios.get(`${API}/availability`, { params: { date: format(f.preferred_date, "yyyy-MM-dd") } })
      .then(({ data }) => { setSlots(data.all || data.slots); setBooked(data.booked || []); setClosedReason(data.closed ? data.reason : ""); })
      .catch(() => { setSlots([]); setBooked([]); })
      .finally(() => setSlotsLoading(false));
  }, [f.preferred_date, refreshKey]);
  const freeCount = slots.filter((t) => !booked.includes(t)).length;

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const isClosedDay = (d) => { const s = format(d, "yyyy-MM-dd"); return closures.some((c) => s >= c.start_date && s <= c.end_date); };
  const isMobile = f.service_type === "mobile";
  const onlinePrice = !isMobile ? cfg.online[f.service] : undefined;
  const payOnline = !!onlinePrice && f.workshop_pay === "online";

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) { toast.error("Please enter your name and email."); return; }
    if (!f.preferred_date || !f.preferred_time) { toast.error("Please choose a preferred date and time."); return; }
    setLoading(true);
    try {
      const payload = { ...f, preferred_date: format(f.preferred_date, "yyyy-MM-dd"), payment_method: isMobile ? f.payment_method : (payOnline ? "online" : "none"), origin_url: window.location.origin };
      delete payload.workshop_pay;
      const { data } = await axios.post(`${API}/enquiries`, payload);
      if (data.checkout_url) {
        toast.message(payOnline ? "Redirecting to secure checkout (card, Klarna or PayPal)..." : "Redirecting to secure payment...");
        window.location.href = data.checkout_url;
        return;
      }
      if (data.bank) {
        setBankInfo({ ...data.bank, reference: data.reference, amount: data.deposit_amount });
      } else {
        toast.success("Booking request sent – pending confirmation", { description: `Reference ${data.reference}. We've emailed you a copy and will email again once your booking is confirmed.`, duration: 8000 });
      }
      setF(empty);
    } catch (err) {
      const d = err?.response?.data?.detail;
      if (err?.response?.status === 409) { setF((prev) => ({ ...prev, preferred_time: "" })); setRefreshKey((k) => k + 1); }
      toast.error(typeof d === "string" ? d : "Something went wrong. Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success("Copied"); };

  const TypeCard = ({ value, icon: Icon, title, sub }) => (
    <button type="button" onClick={() => setF({
        ...f,
        service_type: value,
        service: value === "mobile" ? MOBILE_SERVICE_OPTIONS[0] : (SERVICE_OPTIONS.includes(f.service) ? f.service : SERVICE_OPTIONS[0]),
      })}
      className={`flex items-start gap-3 rounded-xl border p-4 text-left transition-colors ${f.service_type === value ? "border-brand bg-brand/10" : "border-white/10 bg-[#0b0c0f] hover:border-white/25"}`}
      data-testid={`service-type-${value}`}>
      <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${f.service_type === value ? "text-brand" : "text-white/50"}`} />
      <div>
        <div className="text-[15px] font-semibold text-white">{title}</div>
        <div className="mt-0.5 text-[12.5px] text-white/55">{sub}</div>
      </div>
    </button>
  );

  const PayOption = ({ value, icon: Icon, title, sub }) => (
    <button type="button" onClick={() => setF({ ...f, payment_method: value })}
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${f.payment_method === value ? "border-brand bg-brand/10" : "border-white/10 bg-[#0b0c0f] hover:border-white/25"}`}
      data-testid={`payment-method-${value}`}>
      <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${f.payment_method === value ? "border-brand" : "border-white/30"}`}>
        {f.payment_method === value && <span className="h-2 w-2 rounded-full bg-brand" />}
      </span>
      <Icon className="h-4 w-4 text-white/70" />
      <div><div className="text-sm font-semibold text-white">{title}</div><div className="text-[12px] text-white/50">{sub}</div></div>
    </button>
  );

  return (
    <section id="book" className="bg-[#0d0e12] py-24 lg:py-28">
      <div className="container-x grid gap-14 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden rounded-2xl border border-white/10">
            <img src={IMAGES.engine} alt="Engine performance tuning" className="h-[300px] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
          <div className="mt-8"><Badge>{mode === "booking" ? "Book Online" : "Get In Touch"}</Badge></div>
          <h2 className="mt-5 font-display text-[40px] font-bold leading-tight text-white sm:text-[48px]" data-testid="form-heading">{mode === "booking" ? "Book Your Appointment" : "Questions & Quotes"}</h2>
          <p className="mt-4 text-lg leading-relaxed text-white/55">{mode === "booking"
            ? "Pick a date and time that suits you. We'll review your booking and email you a confirmation."
            : "Not ready to book yet? Ask us anything or request a personalised quote. No booking or payment needed."}</p>
          <ul className="mt-8 space-y-4">
            {["Free, no-obligation consultation", "Mobile service across North West London", "Safe, reliable, tailored tuning"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-[15px] text-white/80"><CheckCircle2 className="h-5 w-5 text-brand" />{t}</li>
            ))}
          </ul>
          <div className="mt-8 rounded-2xl border border-white/10 bg-[#111217] p-6" data-testid="opening-hours">
            <div className="flex items-center gap-2 font-display text-xl font-bold text-white"><Clock className="h-5 w-5 text-brand" />Working Hours</div>
            <div className="mt-4 divide-y divide-white/10">
              {OPENING_HOURS.map((o) => (
                <div key={o.days} className="flex items-center justify-between py-3 text-[15px]">
                  <span className="text-white/70">{o.days}</span>
                  <span className="font-semibold text-white">{o.hours}</span>
                </div>
              ))}
            </div>
          </div>
        </Reveal>
        <Reveal delay={120}>
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-white/10 bg-[#111217] p-1" role="tablist" data-testid="form-mode-toggle">
            {[["booking", "Book Now", CalendarCheck], ["enquiry", "Questions & Quotes", MessagesSquare]].map(([m, label, Icon]) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} onClick={() => setMode(m)}
                className={`flex items-center justify-center gap-2 rounded-lg py-3 text-[15px] font-semibold transition-colors ${mode === m ? "bg-brand text-white shadow-[0_8px_24px_-10px_rgba(239,28,37,.7)]" : "text-white/60 hover:bg-white/5 hover:text-white"}`}
                data-testid={`form-mode-${m}`}>
                <Icon className="h-4 w-4" />{label}
              </button>
            ))}
          </div>
          {mode === "enquiry" ? (
            <div className="rounded-2xl border border-white/10 bg-[#111217] p-6 sm:p-9"><EnquiryForm /></div>
          ) : (
          <form onSubmit={submit} className="rounded-2xl border border-white/10 bg-[#111217] p-6 sm:p-9" data-testid="quote-form">
            <div className="mb-6">
              <Label>Service Location *</Label>
              <div className="grid gap-3 sm:grid-cols-2">
                <TypeCard value="workshop" icon={Warehouse} title="Workshop Visit" sub="Bring your car to us · No upfront payment" />
                <TypeCard value="mobile" icon={Truck} title="Mobile Service" sub={`We come to you · £${fmt(DEPOSIT)} deposit`} />
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <div><Label>Full Name *</Label><Input className={fieldCls} placeholder="John Smith" value={f.name} onChange={set("name")} data-testid="input-name" /></div>
              <div><Label>Email *</Label><Input type="email" className={fieldCls} placeholder="john@email.com" value={f.email} onChange={set("email")} data-testid="input-email" /></div>
            </div>
            <div className="mt-5"><Label>Phone</Label><Input className={fieldCls} placeholder="07123 456789" value={f.phone} onChange={set("phone")} data-testid="input-phone" /></div>
            <div className="mt-5 grid grid-cols-3 gap-4">
              <div><Label>Make</Label><Input className={fieldCls} placeholder="BMW" value={f.make} onChange={set("make")} data-testid="input-make" /></div>
              <div><Label>Model</Label><Input className={fieldCls} placeholder="335i" value={f.model} onChange={set("model")} data-testid="input-model" /></div>
              <div><Label>Year</Label><Input className={fieldCls} placeholder="2015" value={f.year} onChange={set("year")} data-testid="input-year" /></div>
            </div>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Preferred Date *</Label>
                <Popover open={calOpen} onOpenChange={setCalOpen}>
                  <PopoverTrigger asChild>
                    <button type="button" className={`${fieldCls} flex w-full items-center gap-2 border px-3 text-left text-sm ${f.preferred_date ? "text-white" : "text-white/35"}`} data-testid="date-picker-trigger">
                      <CalendarDays className="h-4 w-4 text-brand" />
                      {f.preferred_date ? format(f.preferred_date, "EEE d MMM yyyy") : "Pick a date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto border-white/10 bg-[#111217] p-0 text-white" align="start">
                    <Calendar mode="single" selected={f.preferred_date} disabled={(d) => d < today || isClosedDay(d)}
                      onSelect={(d) => { setF({ ...f, preferred_date: d || null, preferred_time: "" }); setCalOpen(false); }}
                      classNames={{ day_selected: "bg-brand text-white hover:bg-brand focus:bg-brand", day_today: "border border-brand/50" }}
                      initialFocus data-testid="date-calendar" />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label>Preferred Time *</Label>
                <Select value={f.preferred_time} onValueChange={(v) => setF({ ...f, preferred_time: v })} disabled={!f.preferred_date || slotsLoading || freeCount === 0}>
                  <SelectTrigger className={fieldCls} data-testid="time-select">
                    <SelectValue placeholder={!f.preferred_date ? "Choose a date first" : slotsLoading ? "Loading..." : closedReason ? `Closed · ${closedReason}` : freeCount === 0 ? "Fully booked" : "Pick a time"} />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#111217] text-white">
                    {slots.map((t) => {
                      const taken = booked.includes(t);
                      return (
                        <SelectItem key={t} value={t} disabled={taken} className="focus:bg-brand/20 focus:text-white data-[disabled]:opacity-40" data-testid={`time-slot-${t}`}>
                          <span className={taken ? "line-through" : ""}>{t} – {endOf(t)}</span>{taken && <span className="ml-2 text-[11px] font-semibold uppercase text-brand">Booked</span>}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="mt-2 flex items-center gap-1.5 text-[12px] text-white/45"><Clock className="h-3.5 w-3.5" />Mon–Fri 18:00–23:00 · Sat–Sun 10:00–22:00</p>
            {closures.length > 0 && <p className="mt-1 text-[12px] text-white/45" data-testid="closures-note">Closed: {closures.slice(0, 3).map((c) => `${c.reason} ${format(new Date(c.start_date + "T00:00"), "d MMM")}${c.end_date !== c.start_date ? `–${format(new Date(c.end_date + "T00:00"), "d MMM")}` : ""}`).join(" · ")}</p>}
            <div className="mt-5">
              <Label>Service Required</Label>
              <Select value={f.service} onValueChange={(v) => { if (v) setF((prev) => ({ ...prev, service: v })); }}>
                <SelectTrigger className={fieldCls} data-testid="select-service"><SelectValue>{f.service}</SelectValue></SelectTrigger>
                <SelectContent className="border-white/10 bg-[#111217] text-white">
                  {(isMobile ? MOBILE_SERVICE_OPTIONS : SERVICE_OPTIONS).map((o) => <SelectItem key={o} value={o} className="focus:bg-brand/20 focus:text-white" data-testid={`service-option-${o}`}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="mt-5"><Label>Additional Details</Label><Textarea rows={4} className={`${fieldCls} h-auto`} placeholder="Tell us about any modifications, goals, or questions..." value={f.details} onChange={set("details")} data-testid="input-details" /></div>
            {onlinePrice && (
              <div className="mt-6 rounded-xl border border-white/10 bg-[#0b0c0f] p-5" data-testid="workshop-payment-box">
                <div className="flex items-center justify-between">
                  <div className="text-[15px] font-semibold text-white">How would you like to pay?</div>
                  <div className="text-right">
                    {cfg.offers[f.service] && <div className="text-[12px] font-semibold text-[#FFC439]" data-testid="booking-offer-label">{cfg.offers[f.service].name} · <span className="text-white/40 line-through">£{Number(cfg.offers[f.service].was).toFixed(2)}</span></div>}
                    <div className="font-display text-2xl font-bold text-white" data-testid="booking-online-price">£{Number(onlinePrice).toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <button type="button" onClick={() => setF({ ...f, workshop_pay: "none" })}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${f.workshop_pay !== "online" ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/25"}`}
                    data-testid="workshop-pay-later">
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${f.workshop_pay !== "online" ? "border-brand" : "border-white/30"}`}>{f.workshop_pay !== "online" && <span className="h-2 w-2 rounded-full bg-brand" />}</span>
                    <Store className="h-4 w-4 text-white/70" />
                    <div><div className="text-sm font-semibold text-white">Pay at workshop</div><div className="text-[12px] text-white/50">Nothing to pay today</div></div>
                  </button>
                  <button type="button" onClick={() => setF({ ...f, workshop_pay: "online" })}
                    className={`flex items-center gap-3 rounded-lg border px-4 py-3 text-left transition-colors ${f.workshop_pay === "online" ? "border-brand bg-brand/10" : "border-white/10 hover:border-white/25"}`}
                    data-testid="workshop-pay-online">
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${f.workshop_pay === "online" ? "border-brand" : "border-white/30"}`}>{f.workshop_pay === "online" && <span className="h-2 w-2 rounded-full bg-brand" />}</span>
                    <CalendarClock className="h-4 w-4 text-white/70" />
                    <div><div className="text-sm font-semibold text-white">Pay online now</div><div className="text-[12px] text-white/50">In full or monthly</div></div>
                  </button>
                </div>
                {payOnline && (
                  <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-4" data-testid="online-pay-info">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="flex items-center gap-1.5 rounded-md border border-white/15 bg-white/5 px-2.5 py-1 text-[12px] font-semibold text-white"><CreditCard className="h-3.5 w-3.5" />Card</span>
                      <span className="rounded-md bg-[#FFA8CD] px-2.5 py-1 text-[12px] font-bold text-[#17120F]">Klarna</span>
                      <span className="rounded-md bg-[#FFC439] px-2.5 py-1 text-[12px] font-bold italic text-[#003087]">PayPal</span>
                    </div>
                    <p className="mt-3 text-[13px] leading-relaxed text-white/65">
                      Spread the cost: <span className="font-semibold text-white">3 interest-free payments of £{(onlinePrice / 3).toFixed(2)}</span> with Klarna or PayPal Pay in 3, pay in 30 days, or monthly financing. Choose at checkout.
                    </p>
                    <p className="mt-2 text-[11.5px] text-white/40">Subject to eligibility and status. Klarna and PayPal credit checks may apply. Full refund if we can't take your booking.</p>
                  </div>
                )}
              </div>
            )}
            {isMobile && (
              <div className="mt-6 rounded-xl border border-brand/30 bg-brand/[0.06] p-5" data-testid="deposit-box">
                <div className="flex items-center justify-between">
                  <div className="text-[15px] font-semibold text-white">Mobile service deposit</div>
                  <div className="font-display text-2xl font-bold text-brand">£{Number(DEPOSIT).toFixed(2)}</div>
                </div>
                <p className="mt-1 text-[12.5px] text-white/55">Secures your mobile booking and is deducted from your final price.</p>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <PayOption value="card" icon={CreditCard} title="Pay by card" sub="Secure Stripe checkout" />
                  <PayOption value="bank" icon={Landmark} title="Bank transfer" sub="Get our bank details" />
                </div>
              </div>
            )}
            <button type="submit" disabled={loading} className="btn-red mt-7 flex w-full items-center justify-center gap-2 rounded-lg py-4 text-base font-semibold disabled:opacity-70" data-testid="submit-enquiry">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (isMobile && f.payment_method === "card") || payOnline ? <Lock className="h-5 w-5" /> : <Send className="h-5 w-5" />} {loading ? "Processing..." : isMobile ? (f.payment_method === "card" ? `Pay £${fmt(DEPOSIT)} Deposit & Send` : "Send Enquiry & Get Bank Details") : payOnline ? `Continue to Payment · £${fmt(onlinePrice)}` : "Send Enquiry"}
            </button>
          </form>
          )}
        </Reveal>
      </div>
      <Dialog open={!!bankInfo} onOpenChange={(o) => !o && setBankInfo(null)}>
        <DialogContent className="max-w-md border-white/10 bg-[#111217] text-white" data-testid="bank-dialog">
          <DialogTitle className="font-display text-2xl font-bold">Booking request received</DialogTitle>
          <DialogDescription className="text-white/55">To confirm your mobile booking, please transfer the £{bankInfo?.amount} deposit using the details below. Use your reference so we can match the payment.</DialogDescription>
          {bankInfo && (
            <div className="mt-2 divide-y divide-white/10 rounded-xl border border-white/10 bg-[#0b0c0f]">
              {[["Account name", bankInfo.account_name], ["Sort code", bankInfo.sort_code], ["Account number", bankInfo.account_number], ["Amount", `£${bankInfo.amount}.00`], ["Reference", bankInfo.reference]].map(([l, v]) => (
                <div key={l} className="flex items-center justify-between px-4 py-3">
                  <span className="text-sm text-white/55">{l}</span>
                  <span className="flex items-center gap-2 font-semibold text-white" data-testid={`bank-${l.toLowerCase().replace(/ /g, "-")}`}>
                    <span className={l === "Reference" ? "text-brand" : ""}>{v}</span>
                    <button onClick={() => copy(String(v))} className="text-white/40 transition-colors hover:text-brand" aria-label={`Copy ${l}`}><Copy className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              ))}
            </div>
          )}
          <p className="text-[12.5px] text-white/45">Your booking is pending confirmation. We'll email you once it's confirmed. The deposit is deducted from your final price, and refunded if we can't take the booking.</p>
          <button onClick={() => setBankInfo(null)} className="btn-red rounded-lg py-3 font-semibold" data-testid="bank-dialog-close">Done</button>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default QuoteForm;
